if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        const id = this.dataset.sectionId;

        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${id}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${id}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${id}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${id}`);
        this.nameInput = this.querySelector(`#Recipient-name-${id}`);
        this.messageInput = this.querySelector(`#Recipient-message-${id}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${id}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${id}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.emailError = this.querySelector(`#RecipientForm-email-error-${id}`);
        this.emailErrorText = this.emailError?.querySelector('.error-message')?.textContent.trim();
        this.messageCounter = this.querySelector(`#RecipientMessageCounter-${id}`);
        this.quantityWarning = this.querySelector(`#RecipientQuantityWarning-${id}`);

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;

        this.externalQuantityInput = document.querySelector('#product-qtda');

        this.addEventListener('change', this.onChange.bind(this));
        this.checkboxInput.addEventListener('change', this.onCheckboxChange.bind(this));
        this.emailInput.addEventListener('blur', this.validateEmail.bind(this));
        this.messageInput.addEventListener('input', this.updateMessageCounter.bind(this));
        if (this.externalQuantityInput) {
          this.externalQuantityInput.addEventListener('change', this.updateQuantityWarning.bind(this));
        }

        this.updateMessageCounter();
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
        } else {
          // Campos ficam desabilitados (não submetem) mas o valor é preservado —
          // o cliente pode religar o toggle sem perder o que já digitou.
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed;
        }
        this.updateQuantityWarning();
      }

      onCheckboxChange() {
        this.onChange();
      }

      updateQuantityWarning() {
        if (!this.quantityWarning) return;
        const qty = this.externalQuantityInput ? parseInt(this.externalQuantityInput.value, 10) || 1 : 1;
        this.quantityWarning.hidden = !(this.checkboxInput.checked && qty > 1);
      }

      updateMessageCounter() {
        if (!this.messageCounter) return;
        const max = parseInt(this.messageCounter.dataset.max, 10) || 200;
        const count = this.messageInput.value.length;
        this.messageCounter.textContent = `${count}/${max}`;
      }

      validateEmail() {
        const value = this.emailInput.value.trim();
        const isValid = value !== '' && this.emailInput.checkValidity();

        if (this.emailInput.disabled) return true;

        if (isValid) {
          this.emailInput.setAttribute('aria-invalid', 'false');
          this.emailError?.classList.add('hidden');
        } else {
          this.emailInput.setAttribute('aria-invalid', 'true');
          const textEl = this.emailError?.querySelector('.error-message');
          if (textEl && this.emailErrorText) textEl.textContent = this.emailErrorText;
          this.emailError?.classList.remove('hidden');
        }

        return isValid;
      }

      validateBeforeSubmit() {
        if (!this.checkboxInput.checked) return true;
        const valid = this.validateEmail();
        if (!valid) this.emailInput.focus();
        return valid;
      }

      getGiftCardProperties() {
        if (!this.checkboxInput.checked) return null;

        const properties = {
          __shopify_send_gift_card_to_recipient: 'on',
          __shopify_offset: this.offsetProperty?.value || new Date().getTimezoneOffset().toString(),
        };

        if (this.emailInput.value) properties['Recipient email'] = this.emailInput.value;
        if (this.nameInput.value) properties['Recipient name'] = this.nameInput.value;
        if (this.messageInput.value) properties['Message'] = this.messageInput.value;
        if (this.sendonInput.value) properties['Send on'] = this.sendonInput.value;

        return properties;
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
        this.updateMessageCounter();
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        if (typeof body === 'object') {
          Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
          return;
        }

        this.errorMessageWrapper.hidden = false;
        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.onChange();
        }
      }
    }
  );
}
